// Combination-based variant selection for items whose `sellable_level` is
// "variant". Each `variant_skus` entry is one sellable combination (one
// variant per group); stock and price live on the SKU, not on the item.
//
// Everything here is pure: groups are the option view's normalised variant
// groups ({ id, options: [{ id, variant_id }] }) and skus are the mapper's
// normalised SKUs (see normalizeVariantSkus).

export const SELLABLE_LEVEL_VARIANT = 'variant';

export function isVariantSellable(product) {
  return (product?.sellableLevel ?? product?.sellable_level) === SELLABLE_LEVEL_VARIANT;
}

function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return Boolean(value);
}

function textOrUndefined(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

// `locations` is documented as an object, but tolerate an array too.
function pickSkuLocation(locations, locationId) {
  if (Array.isArray(locations)) {
    return locations.find((location) => location?.location_id === locationId) ?? locations[0];
  }
  return locations && typeof locations === 'object' ? locations : null;
}

function toImageUrls(images) {
  if (!Array.isArray(images)) return [];
  const sorted = [...images].sort((a, b) => Number(Boolean(b?.is_default)) - Number(a?.is_default));
  return sorted.map((image) => (typeof image === 'string' ? image : image?.url)).filter(Boolean);
}

function findSkuChannel(location, channelId) {
  if (!channelId || !Array.isArray(location?.channels)) return null;
  return location.channels.find((channel) => channel?.channel_id === channelId) ?? null;
}

// A SKU is offered here only when its location is available and the active
// channel is active on it. Flags the payload omits don't block the SKU.
function isSkuAvailableAt(location, channelId) {
  if (!location) return true;
  if (location.is_available !== undefined && !toBoolean(location.is_available)) return false;
  if (!Array.isArray(location.channels) || location.channels.length === 0) return true;
  const isChannelActive = (channel) =>
    channel?.is_active === undefined || toBoolean(channel.is_active);
  if (!channelId) return location.channels.some(isChannelActive);
  const channel = findSkuChannel(location, channelId);
  return Boolean(channel) && isChannelActive(channel);
}

// Same model as the item price: SKU selling_price (or the resolved
// `effective` one when the SKU inherits it) + location delta + channel delta.
function resolveSkuPrice(raw, location, channelId) {
  const basePrice = firstNumber(raw?.selling_price, raw?.effective?.selling_price);
  if (basePrice === undefined) return undefined;
  const channel = findSkuChannel(location, channelId);
  return (
    basePrice + (firstNumber(location?.price_delta) ?? 0) + (firstNumber(channel?.price_delta) ?? 0)
  );
}

export function normalizeVariantSku(raw, { locationId, channelId } = {}) {
  const location = pickSkuLocation(raw?.locations, locationId);
  const combination = Array.isArray(raw?.combination) ? raw.combination : [];
  const variantIds = (
    Array.isArray(raw?.variant_ids) && raw.variant_ids.length
      ? raw.variant_ids
      : combination.map((entry) => entry?.variant_id)
  )
    .filter((id) => id !== undefined && id !== null && id !== '')
    .map(String);
  const trackInventory = toBoolean(location?.online_track_inventory);
  const availableQuantity = firstNumber(location?.available_quantity);

  return {
    id: raw?.item_variant_sku_id,
    locationId: location?.location_id,
    variantIds,
    combination,
    isActive: toBoolean(raw?.is_active),
    isAvailable: isSkuAvailableAt(location, channelId),
    trackInventory,
    availableQuantity,
    isOutOfStock: trackInventory && availableQuantity !== undefined && availableQuantity <= 0,
    sellingPrice: resolveSkuPrice(raw, location, channelId),
    mrp: firstNumber(raw?.mrp, raw?.effective?.mrp),
    images: toImageUrls(raw?.images),
    description: textOrUndefined(raw?.description ?? raw?.effective?.description),
    shortDescription: textOrUndefined(raw?.short_description ?? raw?.effective?.short_description),
    displayOrder: firstNumber(raw?.display_order),
    serviceItems: Array.isArray(raw?.service_items) ? raw.service_items : [],
    serviceItemGroups: Array.isArray(raw?.service_item_groups) ? raw.service_item_groups : [],
  };
}

export function normalizeVariantSkus(list, opts) {
  return Array.isArray(list) ? list.map((raw) => normalizeVariantSku(raw, opts)) : [];
}

// A combination is sellable only when its SKU exists, is active, and is
// available at the current location + channel.
export function getValidSkus(skus) {
  return (Array.isArray(skus) ? skus : []).filter(
    (sku) => sku?.isActive && sku.isAvailable !== false && sku.variantIds.length > 0,
  );
}

// Product-level stock for a variant-sellable item: out of stock when no
// valid combination is in stock. `null` skus = the payload didn't carry
// variant_skus, so stock can't be decided here.
export function isVariantProductOutOfStock(skus) {
  if (!Array.isArray(skus)) return false;
  return !getValidSkus(skus).some((sku) => !sku.isOutOfStock);
}

function optionKey(option) {
  return String(option?.variant_id ?? option?.id ?? '');
}

function skuHas(sku, key) {
  return sku.variantIds.includes(key);
}

function findOption(group, optionId) {
  return group?.options?.find((option) => option.id === optionId) ?? null;
}

// Stable ordering: SKU display_order first, then the position of each of its
// variants within the groups as the API ordered them.
function sortSkus(groups, skus) {
  const positions = (sku) =>
    groups.map((group) => {
      const index = group.options.findIndex((option) => skuHas(sku, optionKey(option)));
      return index < 0 ? Number.MAX_SAFE_INTEGER : index;
    });

  return skus
    .map((sku, index) => ({ sku, index, positions: positions(sku) }))
    .sort((a, b) => {
      const orderA = a.sku.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.sku.displayOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      for (let i = 0; i < a.positions.length; i += 1) {
        if (a.positions[i] !== b.positions[i]) return a.positions[i] - b.positions[i];
      }
      return a.index - b.index;
    })
    .map((entry) => entry.sku);
}

function selectedKeysExcept(groups, selections, excludeGroupId) {
  return groups
    .filter((group) => group.id !== excludeGroupId)
    .map((group) => findOption(group, selections?.[group.id]))
    .filter(Boolean)
    .map(optionKey);
}

export function selectionsFromSku(groups, sku) {
  const selections = {};
  if (!sku) return selections;
  for (const group of groups) {
    const option = group.options.find((item) => skuHas(sku, optionKey(item)));
    if (option) selections[group.id] = option.id;
  }
  return selections;
}

/**
 * Per-option state given the current selections in the *other* groups.
 *  - hasSku:       the variant is in at least one valid SKU
 *  - isCompatible: nothing is selected elsewhere, or a valid SKU exists with
 *                  it + every other current selection (false → faded)
 *  - isOutOfStock: something is selected in another group and every SKU
 *                  matching it + those selections is out of stock (never
 *                  flagged in the default, nothing-selected-elsewhere state)
 * @returns {{ [groupId: string]: { [optionId: string]: { hasSku: boolean, isCompatible: boolean, isOutOfStock: boolean } } }}
 */
export function buildVariantOptionStates(groups, skus, selections) {
  const validSkus = getValidSkus(skus);
  const states = {};

  for (const group of groups) {
    const otherKeys = selectedKeysExcept(groups, selections, group.id);
    states[group.id] = {};
    for (const option of group.options) {
      const key = optionKey(option);
      const withOption = validSkus.filter((sku) => skuHas(sku, key));
      const compatible = withOption.filter((sku) => otherKeys.every((k) => skuHas(sku, k)));
      states[group.id][option.id] = {
        hasSku: withOption.length > 0,
        isCompatible: otherKeys.length === 0 || compatible.length > 0,
        isOutOfStock:
          otherKeys.length > 0 &&
          compatible.length > 0 &&
          compatible.every((sku) => sku.isOutOfStock),
      };
    }
  }

  return states;
}

// The SKU whose combination is exactly the selected variants, else null. A
// SKU need not span every group: a single variant ("Thin Crust") can be a SKU
// on its own while other SKUs combine grouped variants (Size × Portion).
// Variant ids no group lists can't be picked, so they don't block a match.
export function findSelectedSku(groups, skus, selections) {
  const keys = groups
    .map((group) => findOption(group, selections?.[group.id]))
    .filter(Boolean)
    .map(optionKey);
  if (keys.length === 0) return null;

  const listedKeys = new Set(groups.flatMap((group) => group.options.map(optionKey)));
  const matches = getValidSkus(skus).filter(
    (sku) =>
      keys.every((key) => skuHas(sku, key)) &&
      sku.variantIds.every((id) => keys.includes(id) || !listedKeys.has(id)),
  );
  return matches.find((sku) => sku.variantIds.length === keys.length) ?? matches[0] ?? null;
}

// Rebuilds selections for an edited cart line: prefer the stored SKU id, else
// the SKU that contains the most of the line's variant ids.
export function selectionsForCartLine(groups, skus, line) {
  const validSkus = getValidSkus(skus);
  const skuId = line?.itemVariantSkuId;
  const byId = skuId ? validSkus.find((sku) => sku.id === skuId) : null;
  if (byId) return selectionsFromSku(groups, byId);

  const lineKeys = [
    ...(Array.isArray(line?.selectedVariants) ? line.selectedVariants : []),
    ...(line?.variant ? [line.variant] : []),
  ]
    .map((variant) => variant?.variant_id ?? variant?.variantId ?? variant?.id)
    .filter(Boolean)
    .map(String);
  if (lineKeys.length === 0) return null;

  let best = null;
  let bestScore = 0;
  for (const sku of sortSkus(groups, validSkus)) {
    const score = lineKeys.filter((key) => skuHas(sku, key)).length;
    if (score > bestScore) {
      best = sku;
      bestScore = score;
    }
  }
  return best ? selectionsFromSku(groups, best) : null;
}

/**
 * Applies a click on `optionId` in `groupId`. Clicking the selected option
 * clears it. Otherwise it becomes the group's selection, and every other
 * group keeps its selection only while it still forms a valid combination
 * with the new pick (checked in group order); conflicting groups are cleared.
 */
export function applyVariantSelection(groups, skus, selections, groupId, optionId) {
  const group = groups.find((item) => item.id === groupId);
  const option = findOption(group, optionId);
  if (!option) return selections;

  if (selections?.[groupId] === optionId) {
    const next = { ...selections };
    delete next[groupId];
    return next;
  }

  const next = { [groupId]: optionId };
  let candidates = getValidSkus(skus).filter((sku) => skuHas(sku, optionKey(option)));

  for (const other of groups) {
    if (other.id === groupId) continue;
    const current = findOption(other, selections?.[other.id]);
    if (!current) continue;
    const narrowed = candidates.filter((sku) => skuHas(sku, optionKey(current)));
    if (narrowed.length > 0) {
      candidates = narrowed;
      next[other.id] = current.id;
    }
  }

  return next;
}
